// The floating +: Scan and the three raise kinds, one thumb reach from every
// tab. Frame v2.12, template L695-705.
//
// A 60px ink disc at the bottom-right, 88px up so it clears the tab bar,
// with no label — the frame gives it `aria-label="Scan or raise"` and nothing
// written under it, because a disc with a + on it is the one control nobody
// has to be told about. Tapping it rotates the + to an × and fans four
// labelled pills up from it over a scrim: Safety, Quality alert, Andon, Scan
// pod, top to bottom, so Scan is nearest the thumb (see `lib/fab`). Tapping
// the scrim or the × puts them away; tapping a pill puts them away and does
// the thing.
//
// WHY A SPEED-DIAL AND NOT A SHEET. The + used to open a bottom sheet of the
// same four rows. A sheet is a screen change: it darkens everything, it
// has a handle, it asks to be dismissed. A dial is four buttons that appear
// where the thumb already is and go away the moment one is tapped — which is
// the whole difference between "raise" being a place you go and a thing you
// do from wherever you are.
//
// Absolutely positioned inside the shell's frame, which is `position:
// relative`, so on a phone it sits over the main column and above the bar.
// The shell decides whether it is on screen at all (`showFab`); this only
// draws it.

import { Icon } from '@/components/ui/Icon';
import { useT } from '@/i18n/useT';
import { fabItems, type FabItem, type FabItemKey } from '@/lib/fab';

export function PlusFab({ open, onToggle, onChoose }: { open: boolean; onToggle: () => void; onChoose: (key: FabItemKey) => void }) {
  const t = useT();
  return (
    <>
      {open && (
        <>
          {/* The scrim: a tap anywhere else closes the dial. */}
          <button type="button" aria-label={t.dismiss} onClick={onToggle} style={{ position: 'absolute', inset: 0, background: 'rgba(20,22,25,.32)', border: 0, zIndex: 10, cursor: 'default' }} />
          <div
            role="menu"
            aria-label={t.plusPickerTitleT}
            style={{ position: 'absolute', right: 16, bottom: 156, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10, zIndex: 11, animation: 'ipUp .18s ease-out' }}
          >
            {fabItems().map((item) => (
              <Pill key={item.key} item={item} onClick={() => onChoose(item.key)} />
            ))}
          </div>
        </>
      )}
      <button
        type="button"
        className="press"
        aria-label={t.plusPickerTitleT}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={onToggle}
        style={{
          position: 'absolute',
          right: 16,
          bottom: 88,
          width: 60,
          height: 60,
          borderRadius: '50%',
          background: 'var(--ink)',
          color: 'var(--bg)',
          display: 'grid',
          placeItems: 'center',
          boxShadow: '0 12px 26px rgba(22,24,28,.32)',
          zIndex: 12,
          // The + becomes an × by turning, which is what tells a thumb the
          // same tap now closes the dial.
          transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
          transition: 'transform .18s ease',
        }}
      >
        <Icon name="plus" size={28} strokeWidth={2.4} />
      </button>
    </>
  );
}

/** One pill: the label, then the icon tile in the item's tone, nearest the disc. */
function Pill({ item, onClick }: { item: FabItem; onClick: () => void }) {
  const t = useT();
  const tile =
    item.tone === 'ink'
      ? { background: 'var(--ink)', color: 'var(--bg)' }
      : item.tone === 'danger'
        ? { background: 'var(--danger)', color: '#fff' }
        : item.tone === 'warn'
          ? { background: 'var(--warn)', color: '#fff' }
          : { background: 'var(--surface)', color: 'var(--ink)', border: '1px solid var(--line)' };
  return (
    <button
      type="button"
      role="menuitem"
      className="press"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        minHeight: 48,
        padding: '0 8px 0 16px',
        borderRadius: 16,
        background: 'var(--surface)',
        color: 'var(--ink)',
        font: "700 13.5px 'Space Grotesk',sans-serif",
        boxShadow: '0 8px 20px rgba(22,24,28,.22)',
        whiteSpace: 'nowrap',
      }}
    >
      {t[item.label]}
      <span style={{ width: 36, height: 36, borderRadius: 11, display: 'grid', placeItems: 'center', flex: 'none', ...tile }}>
        <Icon name={item.icon} size={18} strokeWidth={2} />
      </span>
    </button>
  );
}
